"use client";

import { type ReactNode } from "react";
import { useClientSidebar } from "./client-sidebar-context";
import { NavFlyoutHost } from "./nav-flyout";

interface SidebarShellProps {
  nav: ReactNode;
}

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

/**
 * The icon rail. Navigation only since the top bar took over branding, the
 * company switcher, demo mode, and the account block (2026-08-01). It hangs
 * below the bar, so it sticks at the bar's height rather than the viewport top.
 */
export function SidebarShell({ nav }: SidebarShellProps) {
  const { collapsed, setCollapsed } = useClientSidebar();

  return (
    <aside
      className={`spark-navigation-surface sticky top-[var(--topbar-h)] hidden h-[calc(100vh-var(--topbar-h))] shrink-0 flex-col bg-spark-navigation py-5 transition-all duration-200 lg:flex ${
        collapsed ? "w-14 px-2" : "w-56 px-4"
      }`}
    >
      <NavFlyoutHost>{nav}</NavFlyoutHost>

      <button
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={`mt-3 flex h-9 items-center gap-2 rounded-lg text-xs font-medium text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong ${
          collapsed ? "mx-auto w-9 justify-center" : "w-full px-3"
        }`}
      >
        <svg {...svgProps}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <polyline points={collapsed ? "13 9 16 12 13 15" : "14 9 11 12 14 15"} />
        </svg>
        {!collapsed && "Collapse"}
      </button>

      {/* The corner belongs to the sticky rail so it stays joined to the top
          bar while scrolling, at either expanded or collapsed width. */}
      <span aria-hidden className="pointer-events-none absolute left-full top-0 z-10 h-6 w-6 bg-spark-navigation print:hidden">
        <span className="block h-full w-full rounded-tl-3xl bg-background" />
      </span>
    </aside>
  );
}
